import type * as C from "@zooi/contracts";
import type { MetricPanel } from "./metric-actions-context.ts";

// Data › Metrics as a dashboard (docs/design-system.md §0.4f): which metric
// the chart shows, and how each period's bar reads. A missing number is "–"
// and a dashed box; a period still under way, or with days missing, is
// striped; a day that is not complete yet is never counted as zero.

export type Metric = C.MetricDashboard["metrics"][number];
export type BarState = "full" | "partial" | "missing" | "pending";

export const WEEKS = ["4", "12"] as const;
export type Weeks = (typeof WEEKS)[number];
export type Grain = C.MetricDashboard["grain"];
/** The data page's tabs; the diagnosis is the default and has no `view` in the address. */
export type DataView = "diagnosis" | "metrics" | "sources" | "definitions" | "money";
export type DataSearch = { view?: Exclude<DataView, "diagnosis">; add?: "cost" | "revenue"; weeks?: 12; grain?: "day"; create?: "metric"; metric?: string; detail?: MetricPanel; primary?: true };

/**
 * Keep configuration and modal deep links inside the product's data page.
 * Until 2026-11-01 the metrics were the default tab: an address without a
 * `view` that sets their range or grain, or adds a metric, still opens them.
 */
export function dataSearch(search: Record<string, unknown>): DataSearch {
  const named = search.view === "metrics" ? "metrics" : search.view === "sources" || search.view === "opportunities" ? "sources" : search.view === "definitions" ? "definitions" : search.view === "money" ? "money" : undefined;
  const range = dashboardSearch(search);
  const view = named ?? (search.view === undefined && (range.weeks || range.grain || search.create === "metric") ? "metrics" : undefined);
  return {
    ...(view ? { view } : {}),
    ...(view === "metrics" ? range : {}),
    ...(view === "money" && (search.add === "cost" || search.add === "revenue") ? { add: search.add } : {}),
    ...(view !== "sources" && search.create === "metric" ? { create: "metric" as const } : {}),
    ...(typeof search.metric === "string" && /^[a-z][a-z0-9_]{1,47}$/.test(search.metric) ? {
      metric: search.metric,
      ...(["trend", "definition", "quality", "usage", "edit", "archive", "delete", "version", "import", "pin"].includes(String(search.detail)) ? { detail: search.detail as MetricPanel } : {}),
      ...dashboardSearch(search),
    } : {}),
    ...(search.primary === true || search.primary === "true" ? { primary: true as const } : {}),
  };
}

/** `?weeks=12&grain=day`; anything else is the default, the last 4 weeks by week. */
export function dashboardSearch(search: Record<string, unknown>): { weeks?: 12; grain?: "day" } {
  return {
    ...(search.weeks === 12 || search.weeks === "12" ? { weeks: 12 as const } : {}),
    ...(search.grain === "day" ? { grain: "day" as const } : {}),
  };
}

export function barState(point: Metric["points"][number], period: C.MetricDashboard["periods"][number]): BarState {
  if (point.value === null) return point.days === 0 ? "pending" : "missing";
  return period.in_progress || point.days_with_data < point.days ? "partial" : "full";
}

/**
 * Which way a small sample moved: only its direction is read, never by how
 * much. Like a change, it needs every day in both ranges.
 */
export function direction(range: Pick<Metric["range"], "value" | "previous" | "complete">): "higher" | "lower" | "same" | null {
  if (!range.complete || range.value === null || range.previous === null) return null;
  return range.value > range.previous ? "higher" : range.value < range.previous ? "lower" : "same";
}

/** The metrics a channel table can split: those whose source reports a channel. */
export const channelMetrics = (metrics: Metric[]) => metrics.filter((m) => !m.presentation?.archived_at && m.breakdown === "available");

/** The metrics a conversion step can be: counts only, by key so a new version keeps the step. */
export function stepChoices(metrics: Metric[]): { key: string; name: string }[] {
  return metrics.filter((m) => m.definition.kind === "count" && !m.presentation?.archived_at).map((m) => ({ key: m.definition.key, name: metricName(m) }));
}

export const metricName = (m: Pick<Metric, "definition" | "presentation">) => m.presentation?.name ?? m.definition.name;
export const metricUnit = (m: Pick<Metric, "definition" | "presentation">) => m.presentation?.unit ?? m.definition.unit;
export const definitionWarning = (m: Pick<Metric, "definition" | "presentation">) => m.definition.kind === "count" && /率|\b(?:rate|ratio)\b/i.test(metricName(m));

/**
 * The metrics tab's order (docs/design-system.md §0.4f, 2026-10-30): the
 * product's primary metric on its own, metrics with a number as cards, and
 * metrics without one as a short list that says what is missing.
 */
export function metricGroups(metrics: Metric[], primaryKey: string | null | undefined): { primary: Metric | null; cards: Metric[]; attention: Metric[] } {
  const primary = metrics.find((m) => m.definition.key === primaryKey) ?? null;
  const rest = metrics.filter((m) => m !== primary);
  return { primary, cards: rest.filter((m) => m.range.value !== null), attention: rest.filter((m) => m.range.value === null) };
}

/** The latest complete day any metric covers: where the page's range ends. */
export function rangeEnd(metrics: Pick<Metric, "range">[]): string | null {
  return metrics.reduce<string | null>((end, m) => end === null || m.range.to > end ? m.range.to : end, null);
}

/**
 * What a card says under its number, at most one short line: how much of the
 * range it covers, a metric that ends before the page does, a time zone other
 * than the product's, and a small sample. Everything longer is in the detail.
 */
export type CardNote = { kind: "coverage"; observed: number; days: number } | { kind: "through"; date: string } | { kind: "zone"; zone: string } | { kind: "sample" };
export function cardNotes(m: Pick<Metric, "definition" | "range">, page: { timeZone: string; end: string | null }): CardNote[] {
  const r = m.range;
  if (r.value === null) return [];
  return [
    ...(r.days_with_data < r.days ? [{ kind: "coverage" as const, observed: r.days_with_data, days: r.days }] : []),
    ...(page.end && r.to < page.end ? [{ kind: "through" as const, date: r.to }] : []),
    ...(m.definition.time_zone !== page.timeZone ? [{ kind: "zone" as const, zone: m.definition.time_zone }] : []),
    ...(r.small_sample ? [{ kind: "sample" as const }] : []),
  ];
}

/** Each period of a card's small bars, with the state the full chart uses. */
export function cardBars(m: Pick<Metric, "points">, periods: C.MetricDashboard["periods"]): { value: number | null; state: BarState; start: string }[] {
  return periods.map((period, i) => {
    const point = m.points[i] ?? { value: null, days: 0, days_with_data: 0 };
    return { value: point.value, state: barState(point, period), start: period.start };
  });
}

/**
 * Where a metric's numbers come from, readable: the service and what is read
 * there (an event, a warehouse table, a package). The definition's own source
 * name stays the record; it can carry schema ids a person does not need here.
 */
export type SourceKind = "posthog_event" | "posthog_table" | "github_stars" | "x_followers" | "npm_downloads" | "pypi_downloads" | "event_count" | "csv" | "other";
export function metricSource(m: Pick<Metric, "definition" | "collector">): { kind: SourceKind; detail: string | null } {
  const source = m.definition.source;
  const subject = m.collector?.subject ?? null;
  if (source === "csv") return { kind: "csv", detail: m.definition.source_name || null };
  if (source === "posthog") {
    let spec: { source?: string; event?: unknown; table_name?: unknown } = {};
    try { spec = subject ? JSON.parse(subject) as typeof spec : {}; } catch { /* An unreadable subject keeps the service only. */ }
    if (spec.source === "warehouse") return { kind: "posthog_table", detail: typeof spec.table_name === "string" ? spec.table_name : null };
    return { kind: "posthog_event", detail: typeof spec.event === "string" ? spec.event : null };
  }
  if (source === "github_stars" || source === "x_followers" || source === "npm_downloads" || source === "pypi_downloads" || source === "event_count") return { kind: source, detail: subject };
  return { kind: "other", detail: m.definition.source_name || null };
}

/** The chart starts on the first primary metric, else the first metric. */
export function chartMetric(metrics: Metric[], chosen: string | null): Metric | null {
  return metrics.find((m) => m.definition.id === chosen) ?? metrics.find((m) => m.primary_for.length > 0) ?? metrics[0] ?? null;
}

/**
 * Where a missing metric is added: an import for a person's own data, the
 * data sources for collected data. Nothing to do while zooi has not collected
 * yet, while events wait for their first complete day, or when a day had no
 * denominator.
 */
export function missingFix(metric: Metric): "import" | "events" | "sources" | null {
  if (metric.missing === null || metric.missing === "no_denominator" || metric.missing === "not_collected_yet") return null;
  if (metric.collector?.last_error === "metric_events_no_full_day") return null;
  if (metric.definition.source === "csv") return "import";
  return metric.collector?.kind === "event_count" ? "events" : "sources";
}
